/**
 * Hero's Journey Page
 * 63-Day transformation journey for adults.
 * 
 * NEW: Summit Beacon Mountain Climb visualization
 * - Abstract, elegant mountain path from Base Camp to Summit
 * - Glowing Summit Beacon that intensifies with progress
 * - Clickable completed days to revisit content
 * - Premium, calm, minimal design (Headspace/Calm aesthetic)
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Compass,
  Check,
  Lock,
  Flame,
  Trophy,
  Eye,
  Brain,
  Heart,
  Sparkles,
  Wind,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Share2,
  RotateCcw,
  Quote,
  Bell,
  BellOff,
  X,
  Clock,
  Mountain,
  Star
} from "lucide-react";
import axios from "axios";
import Confetti from "react-confetti";
import MountainJourneyMap from "../components/MountainJourneyMap";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Theme icons mapping
const THEME_ICONS = {
  presence: Eye,
  subconscious: Brain,
  emotional_mastery: Heart,
  gratitude: Sparkles,
  purpose: Compass,
  flow: Wind,
  identity: Eye,
  values: Sparkles,
  courage: Heart,
  authenticity: Sparkles,
  alignment: Compass,
  creativity: Sparkles
};

// Level colors - refined for mountain theme
const LEVEL_COLORS = {
  1: {
    gradient: "from-indigo-500 to-violet-600",
    bg: "bg-indigo-500",
    text: "text-indigo-400",
    border: "border-indigo-500/30",
    glow: "shadow-indigo-500/20"
  },
  2: {
    gradient: "from-violet-500 to-fuchsia-600",
    bg: "bg-violet-500",
    text: "text-violet-400",
    border: "border-violet-500/30",
    glow: "shadow-violet-500/20"
  },
  3: {
    gradient: "from-amber-500 to-orange-600",
    bg: "bg-amber-500",
    text: "text-amber-400",
    border: "border-amber-500/30",
    glow: "shadow-amber-500/20"
  }
};

// Day Content Modal - Opens when clicking completed/current days on the map
const DayContentModal = ({
  isOpen,
  onClose,
  dayData,
  progress,
  onComplete,
  themeConfig
}) => {
  const [reflectionText, setReflectionText] = useState("");
  const [completing, setCompleting] = useState(false);
  const [showReflectionInput, setShowReflectionInput] = useState(false);

  if (!isOpen || !dayData) return null;

  const day = dayData.day;
  const isCompleted = progress?.completed_days?.includes(day);
  const isCurrent = day === progress?.current_day;
  const savedReflection = progress?.reflections?.[String(day)];
  const theme = dayData.theme || "presence";
  const level = dayData.level || 1;
  const ThemeIcon = THEME_ICONS[theme] || Eye;
  const levelColors = LEVEL_COLORS[level] || LEVEL_COLORS[1];
  const themeStyle = themeConfig?.[theme] || {};

  const handleComplete = async () => {
    if (isCompleted) return;
    setCompleting(true);
    await onComplete(day, reflectionText || null);
    setCompleting(false);
    setShowReflectionInput(false);
    setReflectionText("");
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Content */}
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto bg-gradient-to-br from-[#1a1a2e] to-[#16162a] rounded-t-3xl sm:rounded-3xl border border-white/10"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-b from-[#1a1a2e] to-transparent p-6 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isCompleted
                  ? 'bg-emerald-500 shadow-lg shadow-emerald-500/30'
                  : `bg-gradient-to-br ${levelColors.gradient} shadow-lg ${levelColors.glow}`
              }`}>
                {isCompleted ? (
                  <CheckCircle2 size={24} className="text-white" />
                ) : (
                  <span className="font-bold text-lg text-white">{day}</span>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-semibold text-white">Day {day}</h2>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${levelColors.bg}/20 ${levelColors.text}`}>
                    {themeStyle.label || theme}
                  </span>
                </div>
                <p className="text-white/60 text-sm">{dayData.title}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              <X size={20} className="text-white/60" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pb-6 space-y-4">
          {/* Explanation */}
          <p className="text-white/70 leading-relaxed">
            {dayData.explanation}
          </p>

          {/* Action Box */}
          <div className={`p-4 rounded-xl bg-gradient-to-br ${levelColors.gradient} shadow-lg ${levelColors.glow}`}>
            <div className="flex items-center gap-2 mb-2">
              <ThemeIcon size={16} className="text-white/80" />
              <p className="text-white/80 text-xs font-semibold uppercase tracking-wider">
                Today's Practice
              </p>
            </div>
            <p className="text-white font-medium leading-relaxed">
              {dayData.action}
            </p>
          </div>

          {/* Reflection Prompt */}
          {dayData.reflection_prompt && (
            <div className="p-4 bg-white/5 rounded-xl border border-white/10">
              <p className="text-amber-400/80 text-xs font-semibold uppercase tracking-wider mb-2">
                Reflection
              </p>
              <p className="text-white/70 italic">
                {dayData.reflection_prompt}
              </p>
            </div>
          )}

          {/* Quote */}
          {dayData.quote && (
            <div className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-xl">
              <Quote size={16} className="text-amber-500/60 flex-shrink-0 mt-0.5" />
              <p className="text-white/50 text-sm italic leading-relaxed">
                "{dayData.quote}"
              </p>
            </div>
          )}

          {/* Saved Reflection */}
          {savedReflection && (
            <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
              <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-1">
                Your Reflection
              </p>
              <p className="text-white/80">"{savedReflection}"</p>
            </div>
          )}

          {/* Complete Button / Reflection Input */}
          {!isCompleted && isCurrent && (
            <>
              {showReflectionInput ? (
                <div className="space-y-3">
                  <textarea
                    value={reflectionText}
                    onChange={(e) => setReflectionText(e.target.value)}
                    placeholder="Write your reflection here (optional)..."
                    className="w-full h-24 p-3 rounded-xl border border-white/20 bg-white/5 text-white placeholder-white/30 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                    data-testid="hero-modal-reflection-input"
                  />
                  <div className="flex gap-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleComplete}
                      disabled={completing}
                      className={`flex-1 py-3 rounded-xl bg-gradient-to-r ${levelColors.gradient} text-white font-semibold disabled:opacity-50 shadow-lg`}
                      data-testid="hero-modal-complete-btn"
                    >
                      {completing ? "Completing..." : "Complete Day"}
                    </motion.button>
                    <button
                      onClick={() => setShowReflectionInput(false)}
                      className="px-4 py-3 rounded-xl bg-white/10 text-white/70 hover:bg-white/20"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowReflectionInput(true)}
                  className={`w-full py-3.5 rounded-xl bg-gradient-to-r ${levelColors.gradient} text-white font-semibold shadow-lg ${levelColors.glow}`}
                  data-testid="hero-modal-start-complete-btn"
                >
                  Complete This Practice
                </motion.button>
              )}
            </>
          )}

          {/* Completed Badge */}
          {isCompleted && (
            <div className="flex items-center gap-2 py-2 text-emerald-400">
              <CheckCircle2 size={18} />
              <span className="font-medium">Practice Completed</span>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Journey Complete Celebration
const JourneyCelebration = ({ isOpen, onClose, stats, onShare, onRestart, completedLevel = 3 }) => {
  const [showConfetti, setShowConfetti] = useState(true);
  const [showFinalMessage, setShowFinalMessage] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      
      // For full journey completion, show the special message after initial reveal
      if (completedLevel === 3) {
        const messageTimer = setTimeout(() => setShowFinalMessage(true), 2000);
        return () => {
          clearTimeout(timer);
          clearTimeout(messageTimer);
        };
      }
      return () => clearTimeout(timer);
    } else {
      setShowFinalMessage(false);
    }
  }, [isOpen, completedLevel]);

  if (!isOpen) return null;
  
  const isFullJourney = completedLevel === 3;
  const levelNames = {
    1: "Awakening & Awareness",
    2: "Courage & Transformation", 
    3: "Expansion & Flow"
  };
  const levelTaglines = {
    1: "Threshold 1: Awareness",
    2: "Threshold 2: Transformation",
    3: "Hero Returns"
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
    >
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={isFullJourney ? 300 : 200}
          colors={isFullJourney 
            ? ['#fbbf24', '#f59e0b', '#d97706', '#ffffff', '#f5f5f5'] 
            : ['#f59e0b', '#8b5cf6', '#ec4899', '#10b981', '#3b82f6']
          }
        />
      )}

      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-black/90 backdrop-blur-md"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      />

      {/* Content */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="relative bg-gradient-to-br from-[#1a1a2e] to-[#0f0f1e] rounded-3xl p-8 max-w-sm w-full text-center border border-amber-500/30 shadow-2xl shadow-amber-500/20"
      >
        {/* Glowing icon - Summit burst effect */}
        <motion.div
          animate={{ 
            boxShadow: isFullJourney
              ? ['0 0 30px rgba(251,191,36,0.4)', '0 0 80px rgba(251,191,36,0.8)', '0 0 30px rgba(251,191,36,0.4)']
              : ['0 0 20px rgba(245,158,11,0.3)', '0 0 60px rgba(245,158,11,0.6)', '0 0 20px rgba(245,158,11,0.3)']
          }}
          transition={{ duration: 2, repeat: Infinity }}
          className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${
            isFullJourney 
              ? 'bg-gradient-to-br from-amber-300 via-amber-400 to-orange-500' 
              : 'bg-gradient-to-br from-amber-400 to-orange-500'
          }`}
        >
          {isFullJourney ? (
            <Sparkles size={48} className="text-white" />
          ) : (
            <Trophy size={48} className="text-white" />
          )}
        </motion.div>

        {/* Title with special animation for full journey */}
        <motion.h2 
          className={`font-bold text-white mb-3 ${isFullJourney ? 'text-3xl' : 'text-2xl'}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {levelTaglines[completedLevel]}
        </motion.h2>
        
        {!isFullJourney && (
          <p className="text-amber-400/80 mb-2">
            Level {completedLevel}: {levelNames[completedLevel]}
          </p>
        )}

        {/* Special illuminating message for full journey */}
        {isFullJourney && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: showFinalMessage ? 1 : 0 }}
            transition={{ duration: 1.5 }}
            className="mb-4"
          >
            <p className="text-amber-400/90 text-sm mb-2">You have reached the Summit</p>
            <div className="relative py-4">
              <motion.p 
                className="text-white/90 text-lg font-light italic leading-relaxed"
                animate={showFinalMessage ? {
                  textShadow: ['0 0 10px rgba(251,191,36,0)', '0 0 20px rgba(251,191,36,0.3)', '0 0 10px rgba(251,191,36,0)']
                } : {}}
                transition={{ duration: 3, repeat: Infinity }}
              >
                "The light you were seeking<br/>was always within you."
              </motion.p>
            </div>
            <p className="text-white/50 text-xs mt-2">
              63 days of transformation complete.<br/>
              This is not the end — it is the beginning.
            </p>
          </motion.div>
        )}

        {/* Stats */}
        <motion.div 
          className="grid grid-cols-3 gap-4 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="p-3 bg-white/5 rounded-xl">
            <div className="text-2xl font-bold text-amber-400">{isFullJourney ? 63 : completedLevel * 21}</div>
            <div className="text-xs text-white/50">Days</div>
          </div>
          <div className="p-3 bg-white/5 rounded-xl">
            <div className="text-2xl font-bold text-violet-400">{stats?.streak || 0}</div>
            <div className="text-xs text-white/50">Streak</div>
          </div>
          <div className="p-3 bg-white/5 rounded-xl">
            <div className="text-2xl font-bold text-emerald-400">{stats?.reflections || 0}</div>
            <div className="text-xs text-white/50">Reflections</div>
          </div>
        </motion.div>

        {/* Actions */}
        <div className="space-y-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onShare}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold flex items-center justify-center gap-2"
            data-testid="hero-share-btn"
          >
            <Share2 size={18} />
            Share Your Achievement
          </motion.button>

          {isFullJourney && (
            <button
              onClick={onRestart}
              className="w-full py-3 rounded-xl bg-white/10 text-white/80 font-medium flex items-center justify-center gap-2 hover:bg-white/20 transition-colors"
              data-testid="hero-restart-btn"
            >
              <RotateCcw size={16} />
              Begin New Journey
            </button>
          )}

          <button
            onClick={onClose}
            className="w-full py-2 text-white/50 text-sm hover:text-white/70"
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Notification Settings Modal
const NotificationSettingsModal = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState({
    enabled: false,
    reminder_time: "09:00",
    custom_message: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testSent, setTestSent] = useState(false);

  const getToken = () => localStorage.getItem("reclaim_token");

  useEffect(() => {
    if (isOpen) {
      loadSettings();
    }
  }, [isOpen]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/settings`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.data.hero_journey_notifications) {
        setSettings(res.data.hero_journey_notifications);
      }
    } catch (err) {
      console.error("Error loading settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/settings`,
        { hero_journey_notifications: settings },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      onClose();
    } catch (err) {
      console.error("Error saving settings:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = async () => {
    try {
      await axios.post(
        `${API_URL}/api/push/hero-journey-reminder`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setTestSent(true);
      setTimeout(() => setTestSent(false), 3000);
    } catch (err) {
      console.error("Error sending test:", err);
      alert(err.response?.data?.detail || "Could not send test notification");
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="relative bg-gradient-to-br from-[#1a1a2e] to-[#16162a] rounded-2xl p-6 max-w-sm w-full border border-white/10 shadow-xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Bell size={20} className="text-amber-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Daily Reminders</h3>
              <p className="text-white/50 text-xs">Hero's Journey notifications</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Enable Toggle */}
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
              <div className="flex items-center gap-3">
                {settings.enabled ? (
                  <Bell size={20} className="text-amber-400" />
                ) : (
                  <BellOff size={20} className="text-white/40" />
                )}
                <div>
                  <p className="text-white text-sm font-medium">Enable Reminders</p>
                  <p className="text-white/50 text-xs">Get daily journey nudges</p>
                </div>
              </div>
              <button
                onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
                className={`w-12 h-6 rounded-full transition-colors ${
                  settings.enabled ? 'bg-amber-500' : 'bg-white/20'
                }`}
                data-testid="hero-notification-toggle"
              >
                <motion.div
                  animate={{ x: settings.enabled ? 24 : 2 }}
                  className="w-5 h-5 bg-white rounded-full shadow"
                />
              </button>
            </div>

            {/* Reminder Time */}
            <div className={`p-4 bg-white/5 rounded-xl transition-opacity ${!settings.enabled ? 'opacity-50' : ''}`}>
              <div className="flex items-center gap-2 mb-3">
                <Clock size={16} className="text-white/60" />
                <p className="text-white text-sm font-medium">Reminder Time</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: "07:00", label: "7 AM" },
                  { value: "09:00", label: "9 AM" },
                  { value: "12:00", label: "12 PM" },
                  { value: "18:00", label: "6 PM" },
                  { value: "20:00", label: "8 PM" },
                  { value: "21:00", label: "9 PM" }
                ].map((time) => (
                  <button
                    key={time.value}
                    onClick={() => setSettings({ ...settings, reminder_time: time.value })}
                    disabled={!settings.enabled}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      settings.reminder_time === time.value
                        ? 'bg-amber-500 text-white'
                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    data-testid={`time-option-${time.value}`}
                  >
                    {time.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom message toggle */}
            <div className={`flex items-center justify-between p-4 bg-white/5 rounded-xl ${!settings.enabled ? 'opacity-50' : ''}`}>
              <div>
                <p className="text-white text-sm font-medium">Include Day Content</p>
                <p className="text-white/50 text-xs">Show today's practice title</p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, custom_message: !settings.custom_message })}
                disabled={!settings.enabled}
                className={`w-12 h-6 rounded-full transition-colors ${
                  settings.custom_message ? 'bg-amber-500' : 'bg-white/20'
                } disabled:opacity-50`}
              >
                <motion.div
                  animate={{ x: settings.custom_message ? 24 : 2 }}
                  className="w-5 h-5 bg-white rounded-full shadow"
                />
              </button>
            </div>

            {/* Test button */}
            {settings.enabled && (
              <button
                onClick={handleTestNotification}
                className="w-full py-2 rounded-lg bg-white/10 text-white/70 text-sm hover:bg-white/20 transition-colors flex items-center justify-center gap-2"
                data-testid="hero-test-notification-btn"
              >
                {testSent ? (
                  <>
                    <CheckCircle2 size={16} className="text-emerald-400" />
                    <span className="text-emerald-400">Notification Sent!</span>
                  </>
                ) : (
                  <>
                    <Bell size={16} />
                    Send Test Notification
                  </>
                )}
              </button>
            )}

            {/* Save button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSave}
              disabled={saving}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold disabled:opacity-50"
              data-testid="hero-save-notification-settings"
            >
              {saving ? "Saving..." : "Save Settings"}
            </motion.button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

// Main Page Component
const HeroJourneyPage = () => {
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const [progress, setProgress] = useState(null);
  const [themeConfig, setThemeConfig] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);

  const getToken = () => localStorage.getItem("reclaim_token");

  const loadJourney = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hero-journey`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setJourney(res.data.journey);
      setProgress(res.data.progress);
      setThemeConfig(res.data.theme_config || {});
    } catch (err) {
      console.error("Error loading journey:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJourney();
  }, [loadJourney]);

  const handleStartJourney = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/api/hero-journey/start`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setProgress(res.data.progress);
    } catch (err) {
      console.error("Error starting journey:", err);
    }
  };

  const handleCompleteDay = async (day, reflection) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/hero-journey/day/${day}/complete`,
        { day, reflection },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      if (res.data.journey_complete) {
        setShowCelebration(true);
        setSelectedDay(null);
      } else if (res.data.level_completed) {
        // Show level celebration
        setShowCelebration(true);
        setSelectedDay(null);
      }

      loadJourney();
    } catch (err) {
      console.error("Error completing day:", err);
    }
  };

  const handleRestart = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/api/hero-journey/restart`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      if (res.data.success) {
        setProgress(res.data.progress);
        setShowCelebration(false);
      }
    } catch (err) {
      console.error("Error restarting journey:", err);
    }
  };

  const handleShare = () => {
    navigate("/hero-journey/share");
  };

  const handleDayClick = (day, dayData) => {
    const isCompleted = progress?.completed_days?.includes(day);
    const isCurrent = day === progress?.current_day;
    
    // Only open modal for completed or current days
    if (isCompleted || isCurrent) {
      setSelectedDay(dayData || journey?.days?.find(d => d.day === day));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0a1a] via-[#12122a] to-[#0a0a1a] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <Compass size={40} className="text-amber-500" />
        </motion.div>
      </div>
    );
  }

  // Not started view - Intro screen with Summit Beacon preview
  if (!progress?.started_at) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0a1a] via-[#12122a] to-[#0a0a1a] flex flex-col">
        {/* Stars background effect */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-violet-900/20 via-transparent to-transparent" />
          {[...Array(50)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                opacity: Math.random() * 0.5 + 0.1
              }}
              animate={{
                opacity: [0.1, 0.5, 0.1],
              }}
              transition={{
                duration: 2 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 2
              }}
            />
          ))}
        </div>

        {/* Header */}
        <div className="p-4 relative z-10">
          <button
            onClick={() => navigate("/challenges")}
            className="text-white/60 hover:text-white flex items-center gap-2 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
        </div>

        {/* Hero Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center relative z-10">
          {/* Summit Beacon Preview */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", duration: 0.8 }}
            className="relative mb-8"
          >
            {/* Mountain silhouette with beacon */}
            <div className="relative">
              {/* Glowing beacon at top */}
              <motion.div
                animate={{
                  boxShadow: [
                    '0 0 40px rgba(245,158,11,0.3)',
                    '0 0 80px rgba(245,158,11,0.5)',
                    '0 0 40px rgba(245,158,11,0.3)'
                  ]
                }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-gradient-to-br from-amber-300 to-orange-500 flex items-center justify-center z-10"
              >
                <Star size={16} className="text-amber-100" />
              </motion.div>
              
              {/* Mountain shape */}
              <div className="w-32 h-32 relative">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <defs>
                    <linearGradient id="mountainGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.6" />
                      <stop offset="40%" stopColor="#8b5cf6" stopOpacity="0.4" />
                      <stop offset="100%" stopColor="#6366f1" stopOpacity="0.3" />
                    </linearGradient>
                  </defs>
                  <path 
                    d="M50 10 L85 90 L15 90 Z" 
                    fill="url(#mountainGrad)"
                    stroke="rgba(255,255,255,0.2)"
                    strokeWidth="1"
                  />
                </svg>
              </div>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-3xl font-bold text-white mb-3"
          >
            {journey?.title || "The Hero's Journey"}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-white/60 mb-2 max-w-sm leading-relaxed"
          >
            Climb from Base Camp to Summit
          </motion.p>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="text-white/40 mb-8 max-w-xs text-sm leading-relaxed"
          >
            63 days of inner transformation. The beacon awaits at the summit.
          </motion.p>

          {/* Journey Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex gap-8 mb-10"
          >
            <div className="text-center">
              <div className="text-3xl font-bold text-indigo-400">21</div>
              <div className="text-white/40 text-sm">Days/Level</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-violet-400">3</div>
              <div className="text-white/40 text-sm">Levels</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-amber-400">1</div>
              <div className="text-white/40 text-sm">Summit</div>
            </div>
          </motion.div>

          {/* Start Button */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleStartJourney}
            className="px-10 py-4 rounded-2xl bg-gradient-to-r from-indigo-500 via-violet-500 to-amber-500 text-white font-bold text-lg shadow-2xl shadow-violet-500/30 flex items-center gap-3"
            data-testid="hero-start-journey-btn"
          >
            <Mountain size={24} />
            <span>Step on The Path</span>
          </motion.button>
          
          {/* Level descriptions */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-8 text-center max-w-xs"
          >
            <p className="text-amber-400/60 text-sm font-medium mb-2">
              Your path awaits:
            </p>
            <p className="text-white/40 text-xs leading-relaxed">
              Base Camp → The Path → The Summit
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  // Main journey view with Mountain Map
  const currentLevel = progress?.current_level || 1;
  const completedDays = progress?.completed_days || [];
  const isJourneyComplete = completedDays.length === 63;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a1a] via-[#12122a] to-[#0a0a1a]">
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <button
          onClick={() => navigate("/challenges")}
          className="text-white/60 hover:text-white transition-colors"
        >
          <ArrowLeft size={20} />
        </button>

        <h1 className="text-white font-semibold flex items-center gap-2">
          <Mountain size={18} className="text-amber-400" />
          Hero's Journey
        </h1>

        <div className="flex items-center gap-2">
          {/* Notification Settings Button */}
          <button
            onClick={() => setShowNotificationSettings(true)}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            title="Reminder Settings"
            data-testid="hero-notification-settings-btn"
          >
            <Bell size={16} className="text-amber-400" />
          </button>
          
          {progress?.streak > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-500/20 border border-orange-500/30">
              <Flame size={14} className="text-orange-400" />
              <span className="text-orange-300 font-semibold text-sm">{progress.streak}</span>
            </div>
          )}
        </div>
      </div>

      {/* Mountain Journey Map */}
      <div className="px-4">
        <MountainJourneyMap
          progress={progress}
          journey={journey}
          onDayClick={handleDayClick}
          containerHeight={420}
        />
      </div>

      {/* Current Day Quick Action */}
      {!isJourneyComplete && (
        <div className="px-6 mt-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              const currentDayData = journey?.days?.find(d => d.day === progress?.current_day);
              if (currentDayData) setSelectedDay(currentDayData);
            }}
            className={`w-full p-4 rounded-2xl bg-gradient-to-r ${LEVEL_COLORS[currentLevel].gradient} shadow-xl ${LEVEL_COLORS[currentLevel].glow} flex items-center gap-4`}
            data-testid="hero-current-day-btn"
          >
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <span className="font-bold text-lg text-white">{progress?.current_day}</span>
            </div>
            <div className="flex-1 text-left">
              <div className="text-white font-semibold">
                Day {progress?.current_day}: {journey?.days?.find(d => d.day === progress?.current_day)?.title}
              </div>
              <div className="text-white/70 text-sm">Tap to view today's practice</div>
            </div>
            <ChevronUp size={20} className="text-white/60 rotate-90" />
          </motion.button>
        </div>
      )}

      {/* Always Visible Share Button */}
      {completedDays.length > 0 && (
        <div className="px-6 mt-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/hero-journey/share")}
            className="w-full p-4 rounded-xl bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 flex items-center gap-4"
            data-testid="hero-share-progress-btn"
          >
            <div className="w-10 h-10 rounded-full bg-amber-500/30 flex items-center justify-center">
              <Share2 size={18} className="text-amber-400" />
            </div>
            <div className="flex-1 text-left">
              <div className="text-white font-medium">Share Your Progress</div>
              <div className="text-white/60 text-xs">Inspire others • Earn credits</div>
            </div>
            <div className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-medium">
              +10 credits
            </div>
          </motion.button>
        </div>
      )}

      {/* Journey Complete Banner */}
      {isJourneyComplete && (
        <div className="px-6 mt-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowCelebration(true)}
            className="w-full p-4 rounded-xl bg-gradient-to-r from-amber-500 via-orange-500 to-violet-600 shadow-xl shadow-amber-500/20 flex items-center gap-4"
            data-testid="hero-journey-complete-btn"
          >
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <Trophy size={24} className="text-white" />
            </div>
            <div className="flex-1 text-left">
              <div className="text-white font-bold">You've Reached the Summit!</div>
              <div className="text-white/80 text-sm">63 Days of Transformation. Tap to celebrate.</div>
            </div>
            <Sparkles size={20} className="text-white/80" />
          </motion.button>
        </div>
      )}

      {/* Bottom padding for safe area */}
      <div className="h-24" />

      {/* Day Content Modal */}
      <AnimatePresence>
        {selectedDay && (
          <DayContentModal
            isOpen={!!selectedDay}
            onClose={() => setSelectedDay(null)}
            dayData={selectedDay}
            progress={progress}
            onComplete={handleCompleteDay}
            themeConfig={themeConfig}
          />
        )}
      </AnimatePresence>

      {/* Celebration Modal */}
      <AnimatePresence>
        {showCelebration && (
          <JourneyCelebration
            isOpen={showCelebration}
            onClose={() => setShowCelebration(false)}
            stats={{
              streak: progress?.streak || 0,
              longest_streak: progress?.longest_streak || 0,
              reflections: Object.keys(progress?.reflections || {}).length
            }}
            onShare={handleShare}
            onRestart={handleRestart}
            completedLevel={currentLevel}
          />
        )}
      </AnimatePresence>

      {/* Notification Settings Modal */}
      <AnimatePresence>
        {showNotificationSettings && (
          <NotificationSettingsModal
            isOpen={showNotificationSettings}
            onClose={() => setShowNotificationSettings(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default HeroJourneyPage;
